import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import AsyncCreatableSelect from "react-select/async-creatable";
import { QueryBuilder, prepareRuleGroup } from "react-querybuilder";
import SelectField from "../../inputs/select";
import Basic from "../../inputs/basic";
import { getGAMEntitiesByTag } from "../../../services/tag";
import {
	customUserDataGAMIntegrationMetrics,
	customUserDataGAMVideoOptions,
	customUserDataGAMDefaultLookback,
	ttlOptions,
	customUserDataGAMIntegration,
	queryBuilderTranslations,
	queryBuilderCombinators,
	queryBuilderOperators,
	queryBuilderDateOperators,
} from "../../../utilities/constants";
import {
	QueryBuilderValueEditor,
	QueryBuilderSelectField,
	QueryBuilderCombinatorSelector,
	QueryBuilderButton,
	QueryBuilderCloseButton,
} from "../../inputs/querybuilder";
import CriterionSummary from "./CriterionSummary";
import "./CustomUserDataCard.scss";

/**
 * CustomUserDataCard - Criteria card for targeting users based on custom user data
 *
 * Supports:
 * - Google Ad Manager (GAM) integration
 * - Custom publisher integrations with query builder
 *
 * Data format:
 * GAM: { type: "gam", gam: { metric, gamCompanies, gamOrders, gamLineItems, gamCreatives,
 *                            count?, lookback?, video? } }
 *   count / lookback (ASB-3720): "N or more in the past D days" — omitted on rules saved before
 *   ASB-3720, which the API/sizer/ArcTag treat as "seen at least once". video: true = video-ad
 *   events only, false = display only, omitted = both. The four entity lists are OR'd and all
 *   optional: all empty = every event of the metric.
 * Custom: { type: "custom", custom: { type, filterBy } }
 */
function CustomUserDataCard({
	data,
	onChange,
	isReadOnly,
	isCollapsed,
	tagId,
	customUserDataConfiguration = { integrations: [], gamIntegrationEnabled: false },
}) {

	// Build available integrations list
	const getAvailableIntegrations = () => {
		// GAM, when the publisher's gamIntegrationEnabled setting is on
		const baseIntegrations = customUserDataConfiguration.gamIntegrationEnabled ? [customUserDataGAMIntegration] : [];
		const customIntegrations = (customUserDataConfiguration.integrations || []).map((el) => ({
			value: `custom|${el.event}`,
			label: `${el.publisherName || "Custom"}: ${el.label}`,
			isCustom: true,
			event: el.event,
			filters: (el.filters || []).map((f) => {
				const filter = {
					name: f.field,
					label: f.label,
					operators: queryBuilderOperators.filter((op) => f.operators?.includes(op.name)),
					customDataType: "custom_user_data",
					autocomplete: false,
					eventName: el.event,
					valueMap: f.valueMap || [],
					tagId: el.tagId,
				};
				switch (f.type) {
					case "integer":
						filter.inputType = "number";
						break;
					case "boolean":
						filter.inputType = "boolean";
						filter.defaultValue = true;
						filter.valueEditorType = "checkbox";
						break;
					case "date":
						filter.inputType = "date";
						filter.operators = queryBuilderDateOperators;
						break;
					case "string":
						filter.inputType = "string";
						filter.autocomplete = true;
						filter.multiValue = true;
						break;
				}
				return filter;
			}),
		}));
		return [...baseIntegrations, ...customIntegrations];
	};

	const availableIntegrations = getAvailableIntegrations();

	// Get currently selected integration option
	const getSelectedIntegration = () => {
		if (!data?.type) return null;
		if (data.type === "gam") {
			return availableIntegrations.find((i) => i.value === "gam");
		}
		if (data.type === "custom" && data.custom?.type) {
			return availableIntegrations.find(
				(i) => i.value === `custom|${data.custom.type}` || i.event === data.custom.type
			);
		}
		return null;
	};

	// Handle integration type change
	const handleIntegrationChange = (selected) => {
		if (!selected) {
			onChange({ type: null });
			return;
		}

		if (selected.value === "gam") {
			onChange({
				type: "gam",
				gam: {
					metric: null,
					gamCompanies: [],
					gamOrders: [],
					gamLineItems: [],
					gamCreatives: [],
				},
			});
		} else if (selected.isCustom) {
			const eventType = selected.event || selected.value.split("|")[1];
			onChange({
				type: "custom",
				custom: {
					type: eventType,
					count: data?.custom?.count || 1,
					lookback: data?.custom?.lookback || 365,
					filterBy: {
						combinator: "or",
						rules: [
							{
								field: selected.filters?.[0]?.name || "",
								operator: selected.filters?.[0]?.operators?.[0]?.name || "",
								value: "",
							},
						],
					},
				},
			});
		}
	};

	// Collapsed view
	if (isCollapsed) {
		return (
			<div className="custom-user-data-card collapsed">
				<CriterionSummary
					type="customUserData"
					data={data}
					customUserDataConfiguration={customUserDataConfiguration?.integrations}
				/>
			</div>
		);
	}

	// Expanded view
	return (
		<div className="custom-user-data-card expanded">
			{/* Integration type selector */}
			<div className="input-wrapper">
				<SelectField
					required={false}
					options={availableIntegrations}
					disabled={isReadOnly}
					selected={getSelectedIntegration()}
					name="custom-user-data-integration"
					label="Select Integration"
					clear={true}
					clearCallback={() => handleIntegrationChange(null)}
					onSubmit={handleIntegrationChange}
				/>
			</div>

			{/* GAM Integration UI */}
			{data?.type === "gam" && (
				<GAMIntegration
					data={data.gam}
					onChange={(gamData) => onChange({ ...data, gam: gamData })}
					isReadOnly={isReadOnly}
					tagId={tagId}
				/>
			)}

			{/* Custom Integration UI */}
			{data?.type === "custom" && (
				<CustomIntegration
					data={data.custom}
					onChange={(customData) => onChange({ ...data, custom: customData })}
					isReadOnly={isReadOnly}
					integration={getSelectedIntegration()}
				/>
			)}
		</div>
	);
}

/**
 * GAM Integration sub-component
 */
function GAMIntegration({ data, onChange, isReadOnly, tagId }) {
	const [selectedMetric, setSelectedMetric] = useState(() => {
		if (data?.metric) {
			return customUserDataGAMIntegrationMetrics.find((m) => m.value === data.metric) || null;
		}
		return null;
	});

	const [selectedCompanies, setSelectedCompanies] = useState(() =>
		(data?.gamCompanies || []).map((el) => ({
			label: el.name !== "Unknown" ? `${el.name} (${el.id})` : `${el.id}`,
			value: `${el.id}|${el.name}`,
		}))
	);

	const [selectedOrders, setSelectedOrders] = useState(() =>
		(data?.gamOrders || []).map((el) => ({
			label: el.name !== "Unknown" ? `${el.name} (${el.id})` : `${el.id}`,
			value: `${el.id}|${el.name}`,
		}))
	);

	const [selectedLineItems, setSelectedLineItems] = useState(() =>
		(data?.gamLineItems || []).map((el) => ({
			label: el.name !== "Unknown" ? `${el.name} (${el.id})` : `${el.id}`,
			value: `${el.id}|${el.name}`,
		}))
	);

	const [selectedCreatives, setSelectedCreatives] = useState(() =>
		(data?.gamCreatives || []).map((el) => ({
			label: el.name !== "Unknown" ? `${el.name} (${el.id})` : `${el.id}`,
			value: `${el.id}|${el.name}`,
		}))
	);

	// ASB-3720: threshold, lookback and video split. Pre-ASB-3720 rules carry none of these; the
	// defaults below (1 / 365 days / both) reproduce their "seen at least once" meaning.
	const [count, setCount] = useState(data?.count ?? 1);
	const [lookback, setLookback] = useState(data?.lookback ?? customUserDataGAMDefaultLookback);
	const [video, setVideo] = useState(typeof data?.video === "boolean" ? data.video : null);
	const selectedEntityCount =
		selectedCompanies.length + selectedOrders.length + selectedLineItems.length + selectedCreatives.length;

	// Update parent when selections change
	useEffect(() => {
		const parseEntities = (selections) =>
			selections.map((s) => {
				const [id, ...nameParts] = s.value.split("|");
				return { id: parseInt(id, 10), name: nameParts.join("|") || "Unknown" };
			});

		const next = {
			metric: selectedMetric?.value || null,
			gamCompanies: parseEntities(selectedCompanies),
			gamOrders: parseEntities(selectedOrders),
			gamLineItems: parseEntities(selectedLineItems),
			gamCreatives: parseEntities(selectedCreatives),
			count,
			lookback,
		};
		if (typeof video === "boolean") next.video = video; // "both" = key absent
		onChange(next);
	}, [selectedMetric, selectedCompanies, selectedOrders, selectedLineItems, selectedCreatives, count, lookback, video]);

	// Load options from API
	const loadOptions = async (type, query) => {
		try {
			const response = await getGAMEntitiesByTag(tagId, type, query);
			return (response.results || []).map((el) => ({
				label: el.name !== "Unknown" ? `${el.name} (${el.id})` : `${el.id}`,
				value: `${el.id}|${el.name}`,
			}));
		} catch (error) {
			console.error(`Failed to load GAM ${type}:`, error);
			return [];
		}
	};

	const selectTheme = (theme) => ({
		...theme,
		borderRadius: 0,
		colors: {
			...theme.colors,
			primary25: "#B3CF79",
			primary: "#9ACA3C",
		},
	});

	return (
		<div className="gam-integration">
			<SelectField
				required={true}
				options={customUserDataGAMIntegrationMetrics}
				disabled={isReadOnly}
				selected={selectedMetric}
				name="gam-metric"
				label="Integration Metric"
				clear={false}
				onSubmit={(el) => setSelectedMetric(el)}
			/>

			{selectedMetric && (
				<div className="gam-threshold">
					<span className="modifier-text">{selectedMetric.line}</span>
					<SelectField
						name="gam-video"
						options={customUserDataGAMVideoOptions.map((opt) => ({ ...opt, label: opt.inline }))}
						selected={(() => {
							const opt = customUserDataGAMVideoOptions.find((o) => o.video === video) || customUserDataGAMVideoOptions[0];
							return { ...opt, label: opt.inline };
						})()}
						onSubmit={(opt) => setVideo(opt ? opt.video : null)}
						disabled={isReadOnly}
						required={false}
						search={false}
						clear={false}
						width="medium"
					/>
					<span className="modifier-text">ads</span>
					<Basic
						name="gam-count"
						required={true}
						value={count == null ? "" : String(count)}
						disabled={isReadOnly}
						customClasses="count-input"
						onInput={(e) => setCount(parseInt(e.target.value, 10) || null)}
					/>
					<span className="modifier-text">{selectedMetric.line2}</span>
					<SelectField
						name="gam-lookback"
						options={ttlOptions}
						selected={ttlOptions.find((opt) => opt.value === lookback) || { value: lookback, label: `${lookback} days` }}
						onSubmit={(opt) => opt && setLookback(opt.value)}
						disabled={isReadOnly}
						required={true}
						search={true}
						clear={false}
						width="medium"
					/>
					<span className="gam-threshold-hint">
						Video ads are only detected on sites with a JW Player or STN Video integration; elsewhere
						&ldquo;video&rdquo; matches no one.
					</span>
				</div>
			)}

			{selectedMetric && (
				/* ASB-3720: one bordered group for everything that decides WHICH clicks/impressions count.
				   The four entity lists are OR'd on every side (API, sizer, ArcTag) and all optional: with
				   nothing selected the rule means every event of the metric ("any GAM click"), which the
				   badge and hint state plainly so a wide audience is never an accident. */
				<div className="gam-filter-panel">
					<div className="gam-filter-head">
						<div className="gam-filter-title">
							<span className="label">Which {selectedMetric.nounPlural} count</span>
							{selectedEntityCount === 0 ? (
								<span className="gam-filter-hint">
									Every {selectedMetric.noun} counts. Narrow it by choosing{" "}
									<strong>any</strong> of the selections below.
								</span>
							) : (
								<span className="gam-filter-hint">
									A{selectedMetric.noun === "impression" ? "n" : ""} {selectedMetric.noun} counts if it
									matches <strong>any one</strong> of the selections below.
								</span>
							)}
						</div>
						<span className="gam-filter-count">
							{selectedEntityCount === 0 ? `All ${selectedMetric.nounPlural}` : `${selectedEntityCount} selected`}
						</span>
					</div>
					<div className="gam-filter-body">
						<div className="gam-entities">
						<div className="input-wrapper">
							<label className="chips-label">Companies (Advertisers)</label>
							<AsyncCreatableSelect
								classNamePrefix="select"
								className="select-field"
								value={selectedCompanies}
								defaultOptions
								loadOptions={(input) => loadOptions("company", input)}
								isValidNewOption={(input, _, opts) =>
									!isNaN(input) && input.trim() !== "" && !opts.length
								}
								onChange={setSelectedCompanies}
								isDisabled={isReadOnly}
								isMulti
								isSearchable
								hideSelectedOptions={false}
								menuPlacement="auto"
								closeMenuOnSelect
								placeholder="Any company"
								theme={selectTheme}
								formatCreateLabel={(input) => `Add Company (Advertiser) ID: ${input}`}
							/>
						</div>
						<div className="input-wrapper">
							<label className="chips-label">Orders</label>
							<AsyncCreatableSelect
								classNamePrefix="select"
								className="select-field"
								value={selectedOrders}
								defaultOptions
								loadOptions={(input) => loadOptions("order", input)}
								isValidNewOption={(input, _, opts) =>
									!isNaN(input) && input.trim() !== "" && !opts.length
								}
								onChange={setSelectedOrders}
								isDisabled={isReadOnly}
								isMulti
								isSearchable
								hideSelectedOptions={false}
								menuPlacement="auto"
								closeMenuOnSelect
								placeholder="Any order"
								theme={selectTheme}
								formatCreateLabel={(input) => `Add Order ID: ${input}`}
							/>
						</div>
						<div className="input-wrapper">
							<label className="chips-label">Line Items</label>
							<AsyncCreatableSelect
								classNamePrefix="select"
								className="select-field"
								value={selectedLineItems}
								defaultOptions
								loadOptions={(input) => loadOptions("lineitem", input)}
								isValidNewOption={(input, _, opts) =>
									!isNaN(input) && input.trim() !== "" && !opts.length
								}
								onChange={setSelectedLineItems}
								isDisabled={isReadOnly}
								isMulti
								isSearchable
								hideSelectedOptions={false}
								menuPlacement="auto"
								closeMenuOnSelect
								placeholder="Any line item"
								theme={selectTheme}
								formatCreateLabel={(input) => `Add Line Item ID: ${input}`}
							/>
						</div>
						<div className="input-wrapper">
							<label className="chips-label">Creatives</label>
							<AsyncCreatableSelect
								classNamePrefix="select"
								className="select-field"
								value={selectedCreatives}
								defaultOptions
								loadOptions={(input) => loadOptions("creative", input)}
								isValidNewOption={(input, _, opts) =>
									!isNaN(input) && input.trim() !== "" && !opts.length
								}
								onChange={setSelectedCreatives}
								isDisabled={isReadOnly}
								isMulti
								isSearchable
								hideSelectedOptions={false}
								menuPlacement="auto"
								closeMenuOnSelect
								placeholder="Any creative"
								theme={selectTheme}
								formatCreateLabel={(input) => `Add Creative ID: ${input}`}
							/>
						</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

/**
 * Custom Integration sub-component with QueryBuilder
 */
function CustomIntegration({ data, onChange, isReadOnly, integration }) {
	// Build the initial query and stamp react-querybuilder IDs onto the root
	// group and every rule. RQB keys each rule by `rule.id`; if the query we
	// pass in (controlled mode) has no IDs, RQB regenerates them on every
	// render, which remounts the rule subtree and drops input focus after each
	// keystroke (ASB-3219). `prepareRuleGroup` preserves any existing IDs (e.g.
	// when loading a saved audience) and only fills in missing ones.
	const buildInitialQuery = () =>
		prepareRuleGroup(
			data?.filterBy || {
				combinator: "or",
				rules: [
					{
						field: integration?.filters?.[0]?.name || "",
						operator: integration?.filters?.[0]?.operators?.[0]?.name || "",
						value: "",
					},
				],
			}
		);

	const [query, setQuery] = useState(buildInitialQuery);

	// Reset query when switching between different custom integration types
	const customType = data?.type;
	useEffect(() => {
		setQuery(buildInitialQuery());
	}, [customType]);

	useEffect(() => {
		onChange({
			...data,
			filterBy: query,
		});
	}, [query]);

	if (!integration?.filters?.length) {
		return <div className="custom-integration-empty">No filters available for this integration.</div>;
	}

	return (
		<div className="custom-integration">
			<div className="one-or-more-rules">
				<QueryBuilder
					fields={integration.filters}
					query={query}
					onQueryChange={setQuery}
					listsAsArrays
					translations={queryBuilderTranslations}
					combinators={queryBuilderCombinators}
					operators={queryBuilderOperators}
					showCombinatorsBetweenRules
					disabled={isReadOnly}
					controlElements={{
						addGroupAction: () => null,
						valueSelector: QueryBuilderSelectField,
						valueEditor: QueryBuilderValueEditor,
						addRuleAction: QueryBuilderButton,
						removeRuleAction: QueryBuilderCloseButton,
						combinatorSelector: QueryBuilderCombinatorSelector,
					}}
					onRemove={(_ruleOrGroup, _path, q) => q.rules.length > 1}
				/>
			</div>
		</div>
	);
}

CustomUserDataCard.propTypes = {
	data: PropTypes.shape({
		type: PropTypes.string,
		gam: PropTypes.shape({
			metric: PropTypes.string,
			gamCompanies: PropTypes.array,
			gamOrders: PropTypes.array,
			gamLineItems: PropTypes.array,
			gamCreatives: PropTypes.array,
			count: PropTypes.number,
			lookback: PropTypes.number,
			video: PropTypes.bool,
		}),
		custom: PropTypes.shape({
			type: PropTypes.string,
			count: PropTypes.number,
			lookback: PropTypes.number,
			filterBy: PropTypes.object,
		}),
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	isCollapsed: PropTypes.bool,
	tagId: PropTypes.string,
	customUserDataConfiguration: PropTypes.shape({
		integrations: PropTypes.array,
		// The publisher's gamIntegrationEnabled setting: offers the GAM integration.
		gamIntegrationEnabled: PropTypes.bool,
	}),
};

CustomUserDataCard.defaultProps = {
	data: { type: null },
	isReadOnly: false,
	isCollapsed: false,
	tagId: null,
};

// Helper to validate filterBy rules
const isValidFilterByRules = (rules) => {
	if (!rules || rules.length === 0) return false; // At least 1 rule required
	return rules.every((rule) => {
		const hasField = rule.field && rule.field.trim() !== "";
		const hasOperator = rule.operator && rule.operator.trim() !== "";
		let hasValue;
		if (Array.isArray(rule.value)) {
			hasValue = rule.value.some((v) => v !== undefined && v !== null && String(v).trim() !== "");
		} else {
			hasValue = rule.value !== undefined && rule.value !== null && rule.value !== "";
		}
		return hasField && hasOperator && hasValue;
	});
};

// Validation helper
CustomUserDataCard.isValid = (data) => {
	if (!data?.type) return false;

	if (data.type === "gam") {
		const gam = data.gam;
		if (!gam?.metric) return false;
		// ASB-3720: the four entity lists are optional — none selected means every event of the
		// metric ("any GAM click"). When present, count must be >= 1 and lookback 1..365; rules
		// saved before ASB-3720 have neither and stay valid.
		if (gam.count !== undefined && !(Number.isInteger(gam.count) && gam.count >= 1)) return false;
		if (gam.lookback !== undefined && !(Number.isInteger(gam.lookback) && gam.lookback >= 1 && gam.lookback <= 365)) return false;
		return true;
	}

	if (data.type === "custom") {
		const custom = data.custom;
		if (!custom?.type) return false;
		// At least 1 valid filter is required
		return isValidFilterByRules(custom?.filterBy?.rules);
	}

	return false;
};

// Count helper for header display
CustomUserDataCard.getSelectedCount = (data) => {
	if (!data?.type) return 0;

	if (data.type === "gam" && data.gam) {
		return (
			(data.gam.gamCompanies?.length || 0) +
			(data.gam.gamOrders?.length || 0) +
			(data.gam.gamLineItems?.length || 0) +
			(data.gam.gamCreatives?.length || 0)
		);
	}

	if (data.type === "custom" && data.custom?.filterBy?.rules) {
		return data.custom.filterBy.rules.reduce((count, rule) => {
			if (Array.isArray(rule.value)) {
				const nonEmpty = rule.value.filter((v) => v !== undefined && v !== null && String(v).trim() !== "");
				return count + (nonEmpty.length > 0 ? nonEmpty.length : 0);
			}
			return count + (rule.value !== undefined && rule.value !== null && rule.value !== "" ? 1 : 0);
		}, 0);
	}

	return 0;
};

GAMIntegration.propTypes = {
	data: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	tagId: PropTypes.string,
};

CustomIntegration.propTypes = {
	data: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	integration: PropTypes.object,
};

export default CustomUserDataCard;
